import {
	CustomQuoteAccountBillTo,
	CustomQuoteCustomizationDescription,
	CustomQuoteDetailWrapper,
	CustomQuoteItemDetailField,
	CustomQuoteItemDetailTitle,
	CustomQuoteLineItemGrid,
	CustomQuoteTitle
} from "./CustomQuoteDetail.styles";
import { models } from "types/api/viewModels";
import { Header, Modification } from "components/Common/LineItemGrid";
import {
	AccountNumberText,
	BaseItemHeaderText,
	CustomizationDetailsHeaderText,
	CustomQuoteRequestText,
	DoorStyleText,
	FinishText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { useGetBillToInfoQuery } from "features/api/accountApi";
import StyledSummaryCardLogo from "styles/StyledComponents/StyledSummaryCardLogo";
import { brandCheck } from "utils/order";
import BillToBox from "components/Common/BillToShipToBox/BillToBox/BillToBox";
import { getAttributes, getSubheaderAttributes } from "components/NewOrders/utils/NewOrderUtils";
import { OrderType } from "types/declarations/enums.ts";
import {
	BuildNewOrderLineItemsHeader,
	BuildNewOrderLineItemsHeaderWrapper
} from "components/NewOrders/NewOrders.styles";
import { CustomQuoteAttachments } from "../CustomQuoteAttachments/CustomQuoteAttachments.tsx";
import { Skeleton } from "@mui/material";
import { useMemo } from "react";

interface CustomQuoteDetailProps {
	quote: models["CustomQuoteViewModel"] | undefined;
	isLoading?: boolean;
}

export const CustomQuoteDetail = ({ quote, isLoading }: CustomQuoteDetailProps) => {
	const billToInfoQueryArgs = useMemo(
		() => ({ accountId: quote?.accountId, billToId: quote?.billToId }),
		[quote?.accountId, quote?.billToId]
	);
	const { data: billTo } = useGetBillToInfoQuery(billToInfoQueryArgs, {
		skip: !quote?.accountId || !quote?.billToId
	});

	if (isLoading) {
		return (
			<CustomQuoteDetailWrapper isLoading>
				<Skeleton variant="rectangular" />
				<Skeleton
					variant="rectangular"
					height="224px"
				/>
				<div>
					<Skeleton
						variant="rectangular"
						height="36px"
					/>
					<Skeleton
						variant="rectangular"
						height="16px"
					/>
					<Skeleton
						variant="rectangular"
						height="16px"
					/>
				</div>
			</CustomQuoteDetailWrapper>
		);
	}
	return (
		<CustomQuoteDetailWrapper>
			<CustomQuoteTitle>{CustomQuoteRequestText}</CustomQuoteTitle>
			<CustomQuoteAccountBillTo>
				<div>
					<div>{AccountNumberText}</div>
					<div>
						<div>
							<div>{quote?.accountNumber}</div>
							<div>{quote?.accountName}</div>
						</div>
						<div>
							<StyledSummaryCardLogo
								data-testid="custom-quote-account-card-brand-logo"
								parentBrand={quote?.globals?.brand?.code}
								src={
									brandCheck(quote?.globals?.brand?.code)
										? `/assets/manufacture_logos/${quote?.globals?.brand?.code}.png`
										: "/assets/tandem_logos/cwg_logo.png"
								}
								alt={
									brandCheck(quote?.globals?.brand?.description)
										? (quote?.globals?.brand?.description ?? "")
										: "CabinetworksGroup Logo"
								}
							/>
						</div>
					</div>
				</div>
				<BillToBox
					billTo={billTo}
					orderType={OrderType.UNKNOWN}
					hasBorder
					containsSubtotal={false}
					dataTestIdPrefix="custom-quote"
				/>
			</CustomQuoteAccountBillTo>
			<BuildNewOrderLineItemsHeaderWrapper>
				<BuildNewOrderLineItemsHeader>
					<div data-testid="custom-quote-build-label">
						<div>{getAttributes(quote)}</div>
						<div>{getSubheaderAttributes(quote ?? {})}</div>
					</div>
				</BuildNewOrderLineItemsHeader>
			</BuildNewOrderLineItemsHeaderWrapper>
			{quote?.lineItem && (
				<CustomQuoteLineItemGrid
					infoSlot={<CustomQuoteItemDetailTitle>{BaseItemHeaderText}</CustomQuoteItemDetailTitle>}
					mainRowContent={[
						<Header
							key="base-sku-header"
							item={quote?.lineItem}
						/>,
						<CustomQuoteItemDetailField key="base-sku-door-style">
							<div>{DoorStyleText}</div>
							<div data-testid="custom-quote-style-description">
								{quote?.globals?.style?.description ?? UnavailableDataPlaceholderText}
							</div>
						</CustomQuoteItemDetailField>,
						<CustomQuoteItemDetailField key="base-sku-finish">
							<div>{FinishText}</div>
							<div data-testid="custom-quote-finish-description">
								{quote?.globals?.finish?.description ?? UnavailableDataPlaceholderText}
							</div>
						</CustomQuoteItemDetailField>
					]}
					modificationRows={quote?.lineItem.modifications?.map((modification) => (
						<Modification
							description={modification.description}
							key={modification.sku}
							lineNumber={modification.lineItemNumber}
							title={modification.sku}
							values={modification.values}
						/>
					))}
					requiredCategoriesRows={quote?.lineItem.requiredCategories?.map((category) => (
						<Modification
							key={category.categoryName}
							requiredCategory={category}
							lineNumber={undefined}
						/>
					))}
				/>
			)}
			<CustomQuoteCustomizationDescription>
				<div>{CustomizationDetailsHeaderText}</div>
				<div data-testid="custom-quote-customization-description">{quote?.customizationDescription}</div>
			</CustomQuoteCustomizationDescription>
			<CustomQuoteAttachments
				attachmentUrls={quote?.attachmentUrls}
				quoteId={quote?.quoteId}
			/>
		</CustomQuoteDetailWrapper>
	);
};
